import { List, ListItem, Typography } from '@material-ui/core';
import { Badge } from '@mui/material';
import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useLayoutContext } from '../../Context/Layout.context';
import {
  AnnotationDefinitionCriterion,
  AnnotationOnImage,
  AnnotationsCriterionDTO,
  AnnotationType,
} from '../../models/annotation.types';
import { ExamScoringSchema } from '../../models/assessment.types';
import { setCriterionAnnotationSequence } from '../../services/exams/examTaskGroup/examTaskGroup.service';
import AnnotationPreview from '../AnnotationPreview/AnnotationPreview.component';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import { useStyles } from './AnnotationTool.styles';
import _ from 'lodash';

interface AnnotationToolListProps {
  activeCriterion: ExamScoringSchema | null;
  list: AnnotationDefinitionCriterion[];
  activeAnnotationId: string;
  onActivateAnnotation: (id: string) => void;
  annotationOnPage: AnnotationOnImage[] | null;
  noAnswer: boolean | null;
  canSeeAnnotationTool: boolean;
}

interface IDnDstate {
  draggedFrom: number | null;
  draggedTo: number | null;
  isDragging: boolean;
  originalOrder: AnnotationDefinitionCriterion[];
  updatedOrder: AnnotationDefinitionCriterion[];
}

export const AnnotationToolList: React.FC<AnnotationToolListProps> = (props) => {
  const classes = useStyles();
  const { openAlert, highContrastMode } = useLayoutContext();

  const [list, setList] = useState<AnnotationDefinitionCriterion[]>(props.list);
  const [sequence, setSequence] = useState<string[] | undefined>([]);

  useEffect(() => {
    if (!_.isEqual(props.list, list)) {
      setList(props.list);
      props.onActivateAnnotation('');
    }
  }, [props.list]);

  const handleActive = (value: string) => {
    if (props.activeAnnotationId === value) {
      props.onActivateAnnotation('');
    } else {
      props.onActivateAnnotation(value);
    }
  };

  const refreshData = () => {
    setCriterionAnnotationSequence(
      props.activeCriterion !== null ? props.activeCriterion.examScoringSchemaId : '',
      sequence,
    ).then(
      (response: AxiosResponse<AnnotationsCriterionDTO>) => {
        const data = response.data;
      },
      () => {
        console.error();
        openAlert('get_error');
      },
    );
  };

  const initialState = {
    draggedFrom: null,
    draggedTo: null,
    isDragging: false,
    originalOrder: [],
    updatedOrder: [],
  };

  const [dragAndDrop, setDragAndDrop] = useState<IDnDstate>(initialState);

  const onDragStart = (event: React.DragEvent<HTMLElement>) => {
    const initialPosition = Number(event.currentTarget.dataset.position);

    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: initialPosition,
      isDragging: true,
      originalOrder: list,
    });

    // This is only for Firefox.
    event.dataTransfer.setData('text/html', '');
  };

  const onDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();

    let newList: AnnotationDefinitionCriterion[] = dragAndDrop.originalOrder;

    const draggedFrom = dragAndDrop.draggedFrom;

    // TODO - index of the droppable area being hovered - use it to visual effects
    const draggedTo = Number(event.currentTarget.dataset.position);

    const itemDragged = draggedFrom !== null ? newList[draggedFrom] : newList[0];
    const remainingItems = newList.filter(
      (item: AnnotationDefinitionCriterion, index: number) => index !== draggedFrom,
    );

    newList = [...remainingItems.slice(0, draggedTo), itemDragged, ...remainingItems.slice(draggedTo)];

    if (draggedTo !== dragAndDrop.draggedTo) {
      setDragAndDrop({
        ...dragAndDrop,
        updatedOrder: newList,
        draggedTo: draggedTo,
      });
    }
  };

  const onDrop = () => {
    setList(dragAndDrop.updatedOrder);
    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: null,
      draggedTo: null,
      isDragging: false,
    });
    setSequence(
      dragAndDrop.updatedOrder.map((item) =>
        item.scoringSchemaAnnotationId !== undefined ? item.scoringSchemaAnnotationId : '',
      ),
    );
  };

  useEffect(() => {
    if (sequence?.length !== 0) {
      refreshData();
    }
  }, [sequence]);

  const onDragLeave = () => {
    setDragAndDrop({
      ...dragAndDrop,
      draggedTo: null,
    });
  };

  const countAnnotations = (value: string) => {
    const result = (props.annotationOnPage !== null ? props.annotationOnPage : [])
      .filter((item) => item.annotationDefinitionId === value)
      .filter((item) => item.examScoringSchemaId === props.activeCriterion?.examScoringSchemaId).length;
    return result;
  };

  return (
    <>
      <Typography style={{ fontWeight: 600 }} variant="subtitle2">
        Do oceny
      </Typography>
      <List
        className={classes.list}
        component="div"
        style={{
          borderBottom: 'solid 1px rgba(44, 45, 62, 0.3)',
          marginBottom: '20px',
          justifyContent: 'space-around',
        }}
      >
        {list.map((item, index) => {
          return (
            item.gradingAnnotation && (
              <ListItem
                className={props.canSeeAnnotationTool && !props.noAnswer ? classes.listItem : classes.listItemDisabled}
                onClick={() => {
                  !props.canSeeAnnotationTool || !!props.noAnswer ? undefined : handleActive(item.id);
                }}
                key={item.id}
                data-position={index}
                draggable={props.canSeeAnnotationTool && !props.noAnswer}
                disabled={!props.canSeeAnnotationTool || !!props.noAnswer}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragLeave={onDragLeave}
              >
                <TooltipOne title={item && item ? item.name : ''} placement="top">
                  <div className={item.annotationType === AnnotationType.dynamic ? classes.dynamicIcon : ''}>
                    <div
                      className={
                        props.activeAnnotationId !== item.id
                          ? classes.nonActive
                          : highContrastMode
                          ? `${classes.activeColor} ${classes.activeColorBorder}`
                          : classes.activeColor
                      }
                    >
                      <Badge
                        badgeContent={countAnnotations(item.id)}
                        color="primary"
                        max={1000}
                        className={classes.badge}
                      >
                        <AnnotationPreview svg={item.svg} />
                      </Badge>
                    </div>
                  </div>
                </TooltipOne>
              </ListItem>
            )
          );
        })}
      </List>
      <div>
        <Typography style={{ fontWeight: 600 }} variant="subtitle2">
          Do obliczeń
        </Typography>
      </div>
      <List className={classes.list} component="div" style={{ justifyContent: 'space-around' }}>
        {list.map((item, index) => {
          return (
            !item.gradingAnnotation && (
              <ListItem
                className={props.canSeeAnnotationTool && !props.noAnswer ? classes.listItem : classes.listItemDisabled}
                onClick={() => {
                  !props.canSeeAnnotationTool || !!props.noAnswer ? undefined : handleActive(item.id);
                }}
                key={item.id}
                data-position={index}
                draggable={props.canSeeAnnotationTool && !props.noAnswer}
                disabled={!props.canSeeAnnotationTool || !!props.noAnswer}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onDragLeave={onDragLeave}
              >
                <TooltipOne title={item && item ? item.name : ''} placement="top">
                  <div className={item.annotationType === AnnotationType.dynamic ? classes.dynamicIcon : ''}>
                    <div
                      className={
                        props.activeAnnotationId !== item.id
                          ? classes.nonActive
                          : highContrastMode
                          ? `${classes.activeColor} ${classes.activeColorBorder}`
                          : classes.activeColor
                      }
                    >
                      <Badge
                        badgeContent={countAnnotations(item.id)}
                        color="primary"
                        max={1000}
                        className={classes.badge}
                      >
                        <AnnotationPreview svg={item.svg} />
                      </Badge>
                    </div>
                  </div>
                </TooltipOne>
              </ListItem>
            )
          );
        })}
      </List>
    </>
  );
};
